import { cssThemedValue } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { AdCampaignScheduleStrategy } from 'modules/campaigns/types';

import { getAdCampaignDuration } from './utils';
import CampaignFormItem from '../../CampaignFormItem';

export type AdCampaignScheduleDetailsProps = {
  startDate: string;
  endDate?: string;
  scheduleStrategy?: AdCampaignScheduleStrategy;
  action?: ReactNode;
  campaignId?: string;
  'data-testid'?: string;
};

const ScheduleSectionContentContainer = styled.div`
  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    padding-inline-end: 32px;
  }
`;

const StyledTwoColumns = styled.div`
  display: flex;
  gap: 16px;
  padding-block-start: 10px;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledHeader = styled(Typography.Body3)`
  color: ${cssThemedValue('textSecondary')};
  min-width: 64px;
`;

export const AdCampaignScheduleDetails = ({
  startDate,
  endDate,
  scheduleStrategy,
  action,
  campaignId,
  ...props
}: AdCampaignScheduleDetailsProps) => {
  const { locale } = useIntl();

  const startDateObj = DateTime.fromISO(startDate);
  const endDateObj = endDate ? DateTime.fromISO(endDate) : undefined;
  const duration = endDate ? getAdCampaignDuration(startDate, endDate) : undefined;

  return (
    <CampaignFormItem
      data-testid={props['data-testid']}
      action={action}
      title={<FormattedMessage id="campaigns.ads.schedule.campaign-schedule" />}
      value={
        <ScheduleSectionContentContainer>
          {scheduleStrategy && !campaignId && (
            <Typography.Body3>
              <FormattedMessage
                id={
                  scheduleStrategy === 'alwaysOn'
                    ? 'campaigns.ads.schedule.description-always-on'
                    : 'campaigns.ads.schedule.description-select-dates'
                }
              />
            </Typography.Body3>
          )}
          <StyledTwoColumns>
            <StyledColumn>
              <StyledHeader fontWeight="semiBold">
                <FormattedMessage id="campaigns.form.scheduling.start.label" />
              </StyledHeader>
              {endDateObj && (
                <StyledHeader fontWeight="semiBold">
                  <FormattedMessage id="campaigns.form.scheduling.end.label" />
                </StyledHeader>
              )}
              <StyledHeader fontWeight="semiBold">
                <FormattedMessage id="campaigns.form.scheduling.duration.label" />
              </StyledHeader>
            </StyledColumn>

            <StyledColumn>
              <Typography.Body3>{startDateObj.toLocaleString(DateTime.DATE_MED)}</Typography.Body3>
              {endDateObj && <Typography.Body3>{endDateObj.toLocaleString(DateTime.DATE_MED)}</Typography.Body3>}
              <Typography.Body3>
                {duration ? (
                  duration.toLocaleString(locale, {
                    style: 'unit',
                    unit: 'day',
                    unitDisplay: 'long',
                    maximumFractionDigits: 0,
                  })
                ) : (
                  <FormattedMessage id="campaigns.ads.schedule.always-on" />
                )}
              </Typography.Body3>
            </StyledColumn>
          </StyledTwoColumns>
        </ScheduleSectionContentContainer>
      }
    />
  );
};
